import { useEffect, useState } from "react";
import type { DealerPermission, StaffPermission } from "@longjie/shared";
import { ApiError, request } from "./api";
import { Button, Field } from "./components";

type Permission = DealerPermission | StaffPermission;
type Preset = { id: string; name: string; accountType: "STAFF" | "DEALER"; permissions: Permission[] };

export function PermissionPresets<P extends Permission>({ accountType, value, onApply, disabled = false }: {
  accountType: Preset["accountType"];
  value: P[];
  onApply: (permissions: P[]) => void;
  disabled?: boolean;
}) {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [name, setName] = useState("");
  const [naming, setNaming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  useEffect(() => {
    let active = true;
    void request<{ presets: Preset[] }>(`/api/v1/admin/permission-presets?accountType=${accountType}`)
      .then((result) => { if (active) { setPresets(result.presets); setError(""); } })
      .catch((caught) => { if (active) setError(caught instanceof ApiError ? caught.message : "常用权限方案加载失败"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [accountType, retryKey]);
  async function save(update: boolean) {
    if (saving || disabled) return;
    if (!update && !name.trim()) { setError("请填写方案名称"); return; }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const result = await request<{ preset: Preset }>(update ? `/api/v1/admin/permission-presets/${selectedId}` : "/api/v1/admin/permission-presets", {
        method: update ? "PATCH" : "POST",
        body: JSON.stringify(update ? { permissions: value } : { accountType, name: name.trim(), permissions: value }),
      });
      setPresets((previous) => [...previous.filter((preset) => preset.id !== result.preset.id), result.preset]);
      setSelectedId(result.preset.id);
      setNaming(false);
      setName("");
      setMessage(`方案“${result.preset.name}”已保存，以后新增账号可以直接选择。`);
    } catch (caught) { setError(caught instanceof ApiError ? caught.message : "方案保存失败，请重试"); }
    finally { setSaving(false); }
  }
  return <section className="permission-presets form-grid__full" aria-label="常用权限方案">
    <Field label="套用常用权限方案">
      <select value={selectedId} disabled={disabled || loading || saving} onChange={(event) => {
        const selected = presets.find((preset) => preset.id === event.target.value);
        setSelectedId(event.target.value);
        setMessage("");
        if (selected) onApply([...selected.permissions] as P[]);
      }}>
        <option value="">{loading ? "正在加载方案…" : "选择一个方案，直接填入下方勾选"}</option>
        {presets.map((preset) => <option key={preset.id} value={preset.id}>{preset.name}</option>)}
      </select>
    </Field>
    <p className="permission-picker__hint">保存一次，以后选方案自动填入。套用后可微调；更新方案不会改变已有账号。</p>
    <div className="permission-picker__tools">
      <Button type="button" variant="secondary" disabled={disabled || saving} onClick={() => { setNaming(true); setError(""); }}>保存当前勾选为新方案</Button>
      {selectedId && <Button type="button" variant="ghost" disabled={disabled || saving} onClick={() => void save(true)}>用当前勾选更新方案</Button>}
    </div>
    {naming && <div className="permission-presets__save">
      <Field label="方案名称"><input maxLength={80} value={name} disabled={disabled || saving} onChange={(event) => setName(event.target.value)} placeholder={accountType === "STAFF" ? "例如：仓库人员" : "例如：客户仅查订单"} /></Field>
      <Button type="button" loading={saving} disabled={disabled} onClick={() => void save(false)}>保存方案</Button>
      <Button type="button" variant="ghost" disabled={saving} onClick={() => setNaming(false)}>取消保存方案</Button>
    </div>}
    {error && <div className="inline-error" role="alert">{error}{!naming && <Button type="button" variant="ghost" onClick={() => setRetryKey((value) => value + 1)}>重新加载方案</Button>}</div>}
    {message && <p role="status">{message}</p>}
  </section>;
}
